const CART_DRAWER_REFRESH_TIMEOUT_MS = 15000;

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.openRequestId = 0;
    this.openTimeout = undefined;
    this.renderEpoch = 0;
    this.lastCartRefreshEpoch = undefined;
    this.cartRefreshRequestVersion = 0;
    this.cartRefreshRequested = false;
    this.cartRefreshScheduled = false;
    this.cartRefreshInFlight = false;

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close(),
    );
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
  }

  bindOverlay() {
    const overlay = this.querySelector("#CartDrawer-Overlay");
    if (overlay) overlay.addEventListener("click", this.close.bind(this));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.initializeCartDrawerNote();
    if (this.classList.contains("active")) return;

    const openRequestId = ++this.openRequestId;
    if (this.openTimeout) clearTimeout(this.openTimeout);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    this.openTimeout = setTimeout(() => {
      if (openRequestId !== this.openRequestId) return;
      this.openTimeout = undefined;
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        if (
          openRequestId !== this.openRequestId ||
          !this.classList.contains("active")
        )
          return;
        this.refreshFocusTrap();
      },
      { once: true },
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.openRequestId += 1;
    if (this.openTimeout) {
      clearTimeout(this.openTimeout);
      this.openTimeout = undefined;
    }
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  refreshFocusTrap() {
    const containerToTrapFocusOn = this.classList.contains("is-empty")
      ? this.querySelector(".drawer__inner-empty")
      : document.getElementById("CartDrawer");
    const focusElement =
      this.querySelector(".drawer__inner") ||
      this.querySelector(".drawer__close");
    if (containerToTrapFocusOn && focusElement)
      trapFocus(containerToTrapFocusOn, focusElement);
  }

  initializeCartDrawerNote() {
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id,
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open"),
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  captureRenderEpoch() {
    return Number.isInteger(this.renderEpoch) ? this.renderEpoch : 0;
  }

  invalidateRenderEpoch() {
    this.renderEpoch = this.captureRenderEpoch() + 1;
  }

  isRenderEpochCurrent(renderEpoch) {
    return renderEpoch === this.captureRenderEpoch();
  }

  claimCartMutationRender(renderEpoch) {
    if (!this.isRenderEpochCurrent(renderEpoch)) return undefined;
    this.invalidateRenderEpoch();
    if (this.cartRefreshInFlight) this.requestCartRefresh();
    return this.captureRenderEpoch();
  }

  didCartRefreshSupersede(renderEpoch) {
    return (
      Number.isInteger(this.lastCartRefreshEpoch) &&
      this.lastCartRefreshEpoch > renderEpoch
    );
  }

  requestCartRefresh() {
    this.cartRefreshRequestVersion =
      (Number.isInteger(this.cartRefreshRequestVersion)
        ? this.cartRefreshRequestVersion
        : 0) + 1;
    this.cartRefreshRequested = true;
    if (this.cartRefreshScheduled || this.cartRefreshInFlight)
      return;

    this.cartRefreshScheduled = true;
    Promise.resolve()
      .then(() => {
        this.cartRefreshScheduled = false;
        return this.runCartRefresh();
      })
      .catch((error) => console.error(error));
  }

  async runCartRefresh() {
    if (!this.cartRefreshRequested || this.cartRefreshInFlight)
      return;

    this.cartRefreshRequested = false;
    this.cartRefreshInFlight = true;

    try {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const requestVersion = Number.isInteger(this.cartRefreshRequestVersion)
          ? this.cartRefreshRequestVersion
          : 0;
        const renderEpoch = this.captureRenderEpoch();
        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        config.body = JSON.stringify({
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        });
        const controller =
          typeof AbortController === "function" ? new AbortController() : null;
        if (controller) config.signal = controller.signal;
        let timeout;
        const timeoutPromise = new Promise((_resolve, reject) => {
          timeout = setTimeout(() => {
            controller?.abort();
            reject(new Error("Cart refresh timed out"));
          }, CART_DRAWER_REFRESH_TIMEOUT_MS);
        });
        let response;
        let state;
        try {
          response = await Promise.race([
            fetch(window.routes.cart_update_url, config),
            timeoutPromise,
          ]);
          state = await Promise.race([response.json(), timeoutPromise]);
        } finally {
          clearTimeout(timeout);
        }
        if (
          !response.ok ||
          !state ||
          state.status ||
          !Number.isInteger(state.item_count) ||
          state.item_count < 0 ||
          !this.hasSectionTargets()
        )
          throw new Error(state?.description || "Cart refresh failed");

        if (!this.isRenderEpochCurrent(renderEpoch)) {
          if (attempt === 0) this.cartRefreshRequested = false;
          else this.cartRefreshRequested = true;
          continue;
        }

        this.invalidateRenderEpoch();
        const cartRefreshEpoch = this.captureRenderEpoch();
        if (this.renderContents(state, false, cartRefreshEpoch)) {
          this.lastCartRefreshEpoch = cartRefreshEpoch;
          if (this.cartRefreshRequestVersion === requestVersion)
            this.cartRefreshRequested = false;
          Promise.resolve(
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: "cart-drawer-sync",
              cartData: state,
              renderEpoch: cartRefreshEpoch,
            }),
          ).catch((error) => console.error(error));
        }
        break;
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.cartRefreshInFlight = false;
      if (this.cartRefreshRequested) this.requestCartRefresh();
    }
  }

  // `shouldOpen` defaults to closed, which is where Dawn puts it the other way
  // round. Dawn treats this method as the tail of an add to cart, so a caller
  // that passes nothing gets the drawer thrown open; storefront apps read that
  // as the only way to repaint a cart they have just changed, and the shopper
  // gets a cart over the page they were reading. Opening belongs to the caller
  // that knows a shopper asked for it — every add-to-cart path here passes
  // `true` — so an argument nobody supplied cannot mean yes.
  renderContents(
    parsedState,
    shouldOpen = false,
    renderEpoch,
    cartMutationClaimed = false,
  ) {
    let currentRenderEpoch = renderEpoch;
    if (renderEpoch !== undefined && shouldOpen && !cartMutationClaimed)
      currentRenderEpoch = this.claimCartMutationRender(renderEpoch);
    const isCurrentRender =
      renderEpoch === undefined ||
      (currentRenderEpoch !== undefined &&
        this.isRenderEpochCurrent(currentRenderEpoch));
    if (!isCurrentRender) {
      if (shouldOpen) this.open();
      this.requestCartRefresh();
      return false;
    }

    const isCartRefresh = shouldOpen === false && renderEpoch !== undefined;
    this.productId = parsedState.id;
    const replacements = this.getSectionsToRender()
      .map((section) => {
        const sectionElement = this.getSectionElement(section);

        if (!sectionElement) return null;
        return {
          element: sectionElement,
          html: this.getSectionInnerHTML(
            parsedState.sections[section.id],
            section.selector,
          ),
        };
      })
      .filter(Boolean);

    replacements.forEach((replacement) => {
      replacement.element.innerHTML = replacement.html;
    });

    this.bindOverlay();
    this.initializeCartDrawerNote();
    if (isCartRefresh) {
      if (
        Number.isInteger(parsedState.item_count) &&
        parsedState.item_count >= 0
      )
        this.classList.toggle("is-empty", parsedState.item_count === 0);
    }

    if (shouldOpen && !this.classList.contains("active")) this.open();
    else if (this.classList.contains("active"))
      this.refreshFocusTrap();

    return true;
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    const section = this.getSectionDOM(html, selector);
    if (!section) throw new Error(`Missing cart section selector: ${selector}`);
    return section.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionElement(section) {
    return section.selector
      ? document.querySelector(section.selector)
      : document.getElementById(section.id);
  }

  hasSectionTargets() {
    return this.getSectionsToRender().every((section) =>
      Boolean(this.getSectionElement(section)),
    );
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

/*
 * The last thing in the drawer's scrolling list. While it is out of view the list runs on under
 * the footer, and the footer shows its hairline; when everything fits, no line. The drawer's
 * contents are replaced on every cart change, so each render brings a new watcher.
 */
class CartFooterWatch extends HTMLElement {
  connectedCallback() {
    const list = this.closest("cart-drawer-items");
    if (!list || !window.IntersectionObserver) return;
    // On short screens the list does not scroll on its own: the whole drawer does, footer included.
    if (getComputedStyle(list).overflowY === "visible") return;
    // The footer is looked up on each change: a re-render replaces it after the list.
    this.observer = new IntersectionObserver(
      (entries) =>
        this.closest(".drawer__inner")
          ?.querySelector(".drawer__footer")
          ?.classList.toggle("drawer__footer--covered", !entries[entries.length - 1].isIntersecting),
      { root: list },
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

customElements.define("cart-footer-watch", CartFooterWatch);
